import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, Search } from 'lucide-react';
import Header from './components/Header1';
import Footer from './components/Footer1';
import Button from './components/Button';

const Orders = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    const orders = [
        {
            id: 'ORD-2026-001', date: '2026-03-10', status: 'delivered', total: 8999,
            items: [
                { name: 'Chanderi Silk Saree', quantity: 1, image: 'https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=200' },
            ],
            trackingNumber: 'TRK123456789',
        },
        {
            id: 'ORD-2026-002', date: '2026-03-14', status: 'shipped', total: 27998,
            items: [
                { name: 'Banarasi Brocade Lehenga', quantity: 1, image: 'https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=200' },
                { name: 'Cotton Chikankari Kurta', quantity: 1, image: 'https://images.pexels.com/photos/3014853/pexels-photo-3014853.jpeg?auto=compress&cs=tinysrgb&w=200' },
            ],
            trackingNumber: 'TRK987654321',
            expectedDelivery: '2026-03-20',
        },
        {
            id: 'ORD-2026-003', date: '2026-03-18', status: 'processing', total: 6799,
            items: [
                { name: 'Organza Printed Saree', quantity: 1, image: 'https://images.pexels.com/photos/2681751/pexels-photo-2681751.jpeg?auto=compress&cs=tinysrgb&w=200' },
            ],
        },
    ];

    const statusConfig = {
        processing: { icon: Clock, label: 'Processing', color: 'text-yellow-600', bg: 'bg-yellow-50' },
        shipped: { icon: Truck, label: 'Shipped', color: 'text-blue-600', bg: 'bg-blue-50' },
        delivered: { icon: CheckCircle, label: 'Delivered', color: 'text-[#2D6A4F]', bg: 'bg-[#F8FAF8]' },
    };

    const filteredOrders = orders.filter(order => {
        const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.items.some(item => item.name.toLowerCase().includes(searchTerm.toLowerCase()));
        const matchesFilter = filterStatus === 'all' || order.status === filterStatus;
        return matchesSearch && matchesFilter;
    });

    return (
        <div className="min-h-screen bg-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
            <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
            <Header cartCount={3} />

            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10 lg:py-12">
                <h1 className="text-2xl md:text-3xl font-semibold text-[#1A1A1A] mb-6 md:mb-8">My Orders</h1>

                {/* Search and Filter */}
                <div className="flex flex-col md:flex-row gap-3 mb-6 md:mb-8">
                    <div className="flex-1 relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA3AF]" size={18} />
                        <input
                            type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search orders..."
                            className="w-full pl-11 pr-4 py-2.5 md:py-3 border border-[#E5E7EB] text-sm focus:outline-none focus:border-[#2D6A4F]"
                        />
                    </div>
                    <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                        className="px-4 py-2.5 md:py-3 border border-[#E5E7EB] text-sm focus:outline-none focus:border-[#2D6A4F] bg-white">
                        <option value="all">All Orders</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                    </select>
                </div>

                {filteredOrders.length === 0 ? (
                    <div className="text-center py-16">
                        <Package size={56} className="text-[#D1D5DB] mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-[#1A1A1A] mb-2">No orders found</h2>
                        <p className="text-sm text-[#6B7280] mb-6">Start shopping to see your orders here</p>
                        <Button variant="primary" onClick={() => navigate('/template1/shop')}>Start Shopping</Button>
                    </div>
                ) : (
                    <div className="space-y-4 md:space-y-6">
                        {filteredOrders.map(order => {
                            const statusInfo = statusConfig[order.status];
                            const StatusIcon = statusInfo.icon;
                            return (
                                <div key={order.id} className="border border-[#E5E7EB] p-4 md:p-6">
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4 md:mb-5 pb-4 md:pb-5 border-b border-[#E5E7EB]">
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="text-sm md:text-base font-semibold text-[#1A1A1A]">{order.id}</h3>
                                                <span className={`flex items-center gap-1.5 px-2.5 py-0.5 text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}>
                                                    <StatusIcon size={13} /> {statusInfo.label}
                                                </span>
                                            </div>
                                            <p className="text-xs text-[#6B7280]">Ordered on {new Date(order.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                            {order.expectedDelivery && (
                                                <p className="text-xs text-[#6B7280]">Expected: {new Date(order.expectedDelivery).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                                            )}
                                        </div>
                                        <p className="text-lg md:text-xl font-semibold text-[#1A1A1A] mt-2 md:mt-0">{'\u20B9'}{order.total.toLocaleString('en-IN')}</p>
                                    </div>

                                    <div className="space-y-3 mb-4 md:mb-5">
                                        {order.items.map((item, index) => (
                                            <div key={index} className="flex gap-3">
                                                <div className="w-14 h-14 md:w-16 md:h-16 bg-[#F8FAF8] overflow-hidden flex-shrink-0">
                                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-medium text-[#1A1A1A]">{item.name}</h4>
                                                    <p className="text-xs text-[#6B7280]">Qty: {item.quantity}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        {order.trackingNumber && (
                                            <Button variant="secondary" size="sm">
                                                <Truck size={14} className="mr-1.5" /> Track Order
                                            </Button>
                                        )}
                                        <Button variant="ghost" size="sm">View Details</Button>
                                        {order.status === 'delivered' && (
                                            <Button variant="primary" size="sm">Buy Again</Button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <Footer />
        </div>
    );
};

export default Orders;
